import { Component, OnInit, ViewChild } from '@angular/core';
import { PartNumber } from "@models/partNumber.model";
import { TableConfig } from "@app/shared/util/table-config.utils";
import { ToastsManager } from "@app/shared/util/toastr.service";
import { TranslateParser, TranslateService } from "@ngx-translate/core";
import { PartNumbersService } from "@services/part-numbers.service";
import { PartNumberModalComponent } from "@app/roles/admin/pages/partnumbers/components";
import { Log } from "ng2-logger";
import { PermissionService } from "@app/authentication/services/permission.service";
import { AppUtils } from "@app/shared/util/app.utils";
import * as FileSaver from "file-saver";
import { HttpResponse } from "@angular/common/http";
import { MmdmIntegrationStatusResult } from "@models/mmdmIntegrationStatusResult.model";
import { MmdmIntegrationStatus } from "@models/enums/mmdmIntegrationStatus.enum";
import { MessageType } from '@components/message/message-type.enum';
import { ConfirmationModalComponent } from "@app/shared/components";
import { PartNumberStatus } from "@models/enums/part-number-status.enum";
import {AuthenticationService} from "@app/authentication/services/authentication.service";

@Component({
    selector: 'app-partnumber',
    templateUrl: './partnumber.component.html',
    styleUrls: ['./partnumber.component.scss']
})
export class PartNumberManagementComponent implements OnInit {

    @ViewChild(PartNumberModalComponent, {static: true}) partNumberFormModal: PartNumberModalComponent;
    @ViewChild('syncModal', {static: true}) syncModal: ConfirmationModalComponent;

    _logger = Log.create(PartNumberManagementComponent.name);
    partNumbers: PartNumber[] = [];
    tableConfig = new TableConfig();
    syncStatus = new MmdmIntegrationStatusResult();
    isDownloadInProgress = false;
    isLoadingPartNumbers = false;
    isLoadingModal = false;
    isLoadingSyncStatus = false;
    filterStatusList = [];

    public readonly MessageType = MessageType;

    constructor(private partNumberService: PartNumbersService,
                private authService: AuthenticationService,
                private toastr: ToastsManager,
                private permissionService: PermissionService,
                private translationService: TranslateService,
                private translateParser: TranslateParser) {
        this.filterStatusList = this.buildStatusFilterColumnKeys();
        this.translationService.onLangChange.subscribe((event) => {
            this.filterStatusList = this.buildStatusFilterColumnKeys();
        });
    }

    ngOnInit() {
        this.resetSyncStatus();
        this.getPartNumbers(this.tableConfig);
        this.getSyncStatus();
    }

    onFilter(tableConfig: TableConfig) {
        this.getPartNumbers(tableConfig);
    }

    onPageChange(tableConfig: TableConfig) {
        this.getPartNumbers(tableConfig);
    }

    openPartNumberModal() {
        this.partNumberFormModal.open();
    }

    onDuplicateRow(partNumber) {
        this._logger.info('onDuplicateRow::', partNumber);
        if (this.isSyncInProgress()) {
            return;
        }
        partNumber.name = partNumber.name + AppUtils.COPY_STRING;
        this.partNumberFormModal.open(partNumber, true);
    }

    getPartNumbers(tableConfig = this.tableConfig) {
        this.isLoadingPartNumbers = true;
        tableConfig.filters.push({field: 'includeEquivalence', value: true});
        tableConfig.filters.push({field: 'includeRelated', value: true});
        this.partNumberService.getAllPartNumbers(tableConfig).subscribe(data => {
                this.partNumbers = data.partNumbers;
                this.tableConfig.pager.count = data.count;
                this.isLoadingPartNumbers = false;
                this._logger.info("getPartNumbers::Data returned from Api", this.partNumbers);
            },
            (error: any) => {
                this.isLoadingPartNumbers = false;
                this._logger.error("getPartNumbers::", error);
            });
    }

    getSyncStatus() {
        this.isLoadingSyncStatus = true;
        this.partNumberService.getStatusForSyncPartNumbers().subscribe(data => {
                this.syncStatus = data;
                this.isLoadingSyncStatus = false;
                this._logger.info("getSyncStatus::Data returned from Api", this.syncStatus);
            },
            (error: any) => {
                this.isLoadingSyncStatus = false;
                this._logger.error("getSyncStatus::", error);
            }
        )
    }

    createPartNumber(partNumber) {
        this.isLoadingModal = true;
        partNumber.toolIds = partNumber.tools.map(t => t.id);
        this.partNumberService.createPartNumber(partNumber).subscribe(
            (data: any) => {
                this.getPartNumbers();
                this.toastr.success(this.buildCreateMessage(data.name));
                this.isLoadingModal = false;
                this.partNumberFormModal.close();
            },
            (error: any) => {
                this._logger.error("createPartNumber::", error);
                this.isLoadingModal = false;
            }
        );
    }

    updatePartNumber(partNumberUpdate) {
        // This is a short fix to avoid update when the user doesn't have permission
        if (!this.hasPermission()) {
            this.partNumberFormModal.close();
            return;
        }

        this.isLoadingModal = true;
        partNumberUpdate.toolIds = partNumberUpdate.tools.map(t => t.id);
        this.partNumberService.updatePartNumber(partNumberUpdate).subscribe(
            (data: any) => {
                this.getPartNumbers();
                this.toastr.success(this.buildUpdateMessage(partNumberUpdate.previousPartNumberName));
                this.isLoadingModal = false;
                this.partNumberFormModal.close();
            },
            (error: any) => {
                this._logger.error("updatePartNumbers::", error);
                this.isLoadingModal = false;
            }
        );
    }

    deletePartNumber(partNumber) {
        this.isLoadingModal = true;
        this.partNumberService.deletePartNumber(partNumber).subscribe(
            (data: any) => {
                this.getPartNumbers();
                this.toastr.warning(this.buildDeleteMessage(partNumber.name));
                this.isLoadingModal = false;
                this.partNumberFormModal.close();
            },
            (error: any) => {
                this._logger.error("deletePartNumber::", error);
                this.isLoadingModal = false;
            }
        );
    }

    buildCreateMessage(partNumberName) {
        const message = this.translationService.instant('admin.partNumbers.toasts.created');
        return this.translateParser.interpolate(message, {partNumberName: partNumberName});
    }

    buildUpdateMessage(partNumberName) {
        const message = this.translationService.instant('admin.partNumbers.toasts.updated');
        return this.translateParser.interpolate(message, {partNumberName: partNumberName});
    }

    buildDeleteMessage(partNumberName) {
        const message = this.translationService.instant('admin.partNumbers.toasts.deleted');
        return this.translateParser.interpolate(message, {partNumberName: partNumberName});
    }

    onSortChange(tableState) {
        this.getPartNumbers(tableState);
    }

    onTableRowClick(partNumber: PartNumber) {
        if (this.isSyncInProgress()) {
            return;
        }
        this.partNumberFormModal.open(partNumber);
    }

    shouldRenderModifiers() {
        return this.permissionService.hasHasPermissionForFeature('MANAGE_PARTNUMBERS');
    }

    onDownloadPartNumReport(tableConfig = this.tableConfig) {
        tableConfig.filters.push({field: 'includeRelated', value: true});
        this.isDownloadInProgress = true;
        this.partNumberService.downloadPartNumberReport(tableConfig).subscribe(
            (data: HttpResponse<any>) => {
                if (data.body != null) {
                    this._logger.info('onDownloadPartNumReport:: API Returned error', data);
                    const blob = new Blob([data.body], {type: 'application/xsls'});
                    const filename = data.headers.get('x-filename');
                    FileSaver.saveAs(blob, filename);
                }
                this.isDownloadInProgress = false;
            },
            (error: any) => {
                this._logger.error('onDownloadPartNumReport:: API Returned error', error);
                this.isDownloadInProgress = false;
            }
        );
    }

    openSyncModal() {
        this.syncModal.open();
    }

    onSyncPartNumbers() {
        this.syncModal.close();

        this.syncStatus = new MmdmIntegrationStatusResult({
            status: MmdmIntegrationStatus.IN_PROGRESS,
            endTime: null
        });

        this.partNumberService.syncPartNumbers().subscribe(
            (data) => {
                this._logger.info('onSyncPartNumbers:: Api Returned data', data);
                this.toastr.info(this.translationService.instant('admin.partNumbers.table.sync.toasts.start'));
            },
            (error: any) => {
                this._logger.error('onSyncPartNumbers:: API Returned error', error);
                this.toastr.warning(this.translationService.instant('admin.partNumbers.table.sync.toasts.error'));
                this.getSyncStatus();
            }
        );
    }

    isSyncInProgress() {
        return this.syncStatus.status === MmdmIntegrationStatus.IN_PROGRESS;
    }

    buildStatusFilterColumnKeys() {
        return [
            {
                name: this.translationService.instant("generic.statuses.all"),
                value: PartNumberStatus.ALL
            },
            {
                name: this.translationService.instant("generic.statuses.notConfigured"),
                value: PartNumberStatus.NOT_CONFIGURED
            },
            {
                name: this.translationService.instant("generic.statuses.configured"),
                value: PartNumberStatus.CONFIGURED
            },
        ];
    }

    resetSyncStatus() {
        this.syncStatus = new MmdmIntegrationStatusResult({
            status: MmdmIntegrationStatus.NOT_AVAILBLE,
            endTime: null
        });
    }

    hasPermission() {
        return this.authService.hasPermission('MANAGE_PARTNUMBERS');
    }
}
